/**
 * 把命令式 lit-popup 库包装成声明式 React 组件。
 * 外部只需要传 name/opened；本组件负责实例生命周期、Portal、动画 Promise、焦点和全局状态同步。
 */
import { useState, useEffect, useRef, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import LitPopup from 'lit-popup';
import classNames from 'classnames';
import { useOpenedPopupsState } from '../../../atoms/opened-popups';

type Props = React.HTMLAttributes<HTMLElement> & {
    containerClass?: string;
    name: string;
    onOpen?: (instance: LitPopup) => void;
    onOpenComplete?: (instance: LitPopup) => void;
    onClose?: (instance: LitPopup) => void;
    onCloseComplete?: (instance: LitPopup) => void;
    opened?: boolean;
    overlay?: boolean;
    wrapperChildren?: ReactNode;
};

// 弹窗打开时锁住页面滚动；Portal 根节点定义在 index.html 中。
const NO_SCROLL_CLASS = 'no-scroll';
const root = document.querySelector('#portal-root');

const LitPopupComponent = ({
    children,
    wrapperChildren,
    containerClass = '',
    name,
    onOpen,
    onOpenComplete,
    onClose,
    onCloseComplete,
    opened,
    overlay,
    ...props
}: Props) => {
    // 必须和 CSS 的进出场时长一致，Promise 完成后 lit-popup 才认为动画结束。
    const leaveDurationInSeconds = 0.3;
    const closeAnimationTimeout = useRef<ReturnType<typeof setTimeout>>();
    const [instance, setInstance] = useState<LitPopup | null>(null);
    const [openedPopups, setOpenedPopups] = useOpenedPopupsState();

    // 方向一：全局 openedPopups 改变时，同步到命令式实例。
    useEffect(() => {
        if (instance) {
            if (openedPopups.includes(name)) {
                if (!instance.isOpen) {
                    instance.open();
                }
            } else {
                if (instance.isOpen) {
                    instance.close();
                }
            }
        }
    }, [openedPopups, instance, name]);

    // 组件挂载时创建一次 lit-popup 实例，并把库回调桥接到 React props/Recoil。
    useEffect(() => {
        const instance = new LitPopup(name, {
            onOpen: () => {
                // 弹窗打开期间禁止底层页面滚动。
                document.body.classList.add(NO_SCROLL_CLASS);
                onOpen?.(instance);
            },
            onOpenComplete: () => {
                // 如果弹窗内部标记了默认焦点元素，动画结束后把焦点移过去以改善键盘操作。
                const focusableOnOpenElement =
                    instance.el.querySelector<HTMLElement>(
                        '[data-focus-on-popup-open]',
                    );
                setTimeout(
                    () =>
                        focusableOnOpenElement?.focus({ preventScroll: true }),
                    50,
                );
                onOpenComplete?.(instance);
                // Set 去重，避免重复回调导致同名弹窗被记录多次。
                setOpenedPopups((prevPopups) =>
                    Array.from(new Set([...prevPopups, name])),
                );
            },
            onClose: () => {
                onClose?.(instance);
            },
            onCloseComplete: () => {
                onCloseComplete?.(instance);
                document.body.classList.remove(NO_SCROLL_CLASS);
                setOpenedPopups((prevPopups) =>
                    prevPopups.filter((popupName) => popupName !== name),
                );
            },
            // lit-popup 等待 Promise；真正的视觉过渡由 CSS 类完成。
            openAnimation: () =>
                new Promise((resolve) => {
                    clearTimeout(closeAnimationTimeout.current);
                    closeAnimationTimeout.current = setTimeout(
                        resolve,
                        leaveDurationInSeconds * 1000,
                    );
                }),
            closeAnimation: () =>
                new Promise((resolve) => {
                    clearTimeout(closeAnimationTimeout.current);
                    closeAnimationTimeout.current = setTimeout(
                        resolve,
                        leaveDurationInSeconds * 1000,
                    );
                }),
        });
        setInstance(instance);

        return () => {
            // 销毁第三方实例和计时器，防止热更新/卸载后残留事件监听。
            clearTimeout(closeAnimationTimeout.current);
            instance.destroy();
            setInstance(null);
            document.body.classList.remove(NO_SCROLL_CLASS);
        };
    }, [
        name,
        onClose,
        onCloseComplete,
        onOpen,
        onOpenComplete,
        setOpenedPopups,
    ]);

    // 方向二：父组件直接改变 opened prop 时，同步到命令式实例。
    useEffect(() => {
        if (instance) {
            if (opened) {
                if (!instance.isOpen) {
                    instance.open();
                }
            } else if (instance.isOpen) {
                instance.close();
            }
        }
    }, [instance, opened]);

    // 这棵节点树最终通过 createPortal 渲染到 body 末尾的 #portal-root。
    const Component = (
        <div
            {...props}
            className={classNames('lit-popup', props.className, {
                'lit-popup--opened': opened,
            })}
            // 以下 data 属性是 lit-popup 查找实例、触发器和 inert 规则的协议。
            data-lit-popup={name}
            data-lit-popup-preset="default"
            data-lit-popup-ignore-inert
        >
            {overlay && (
                <div
                    className="lit-popup-overlay"
                    data-lit-popup-close={name}
                ></div>
            )}
            {wrapperChildren}
            <div className={classNames('lit-popup-container', containerClass)}>
                {children}
            </div>
        </div>
    );

    return root ? createPortal(Component, root) : null;
};

export default LitPopupComponent;
